import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { apiGet } from '../lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { SearchFilter } from '@/components/SearchFilter';
import { Shield, RefreshCw, LogIn, XCircle, RotateCw } from 'lucide-react';

type AuthEventType = 'login_success' | 'login_failed' | 'session_resumed';

type AuthEvent = {
  id: string;
  account_id: string | null;
  user_id: string | null;
  email: string;
  event_type: AuthEventType;
  failure_reason: string | null;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
};

type AuthEventsResponse = {
  data: AuthEvent[];
  count: number;
};

async function fetchAuthEvents(params: {
  event_type?: AuthEventType;
  email?: string;
  since?: string;
  limit?: number;
}): Promise<AuthEventsResponse> {
  const searchParams = new URLSearchParams();
  if (params.event_type) searchParams.set('event_type', params.event_type);
  if (params.email) searchParams.set('email', params.email);
  if (params.since) searchParams.set('since', params.since);
  if (params.limit) searchParams.set('limit', params.limit.toString());

  const queryString = searchParams.toString();
  const url = `/api/v1/auth-events${queryString ? `?${queryString}` : ''}`;

  return apiGet<AuthEventsResponse>(url);
}

function getEventTypeBadge(type: AuthEventType) {
  switch (type) {
    case 'login_success':
      return {
        label: 'Login Success',
        className: 'bg-green-badge text-green-badge-foreground',
        icon: LogIn,
      };
    case 'login_failed':
      return {
        label: 'Login Failed',
        className: 'bg-red-badge text-red-badge-foreground',
        icon: XCircle,
      };
    case 'session_resumed':
      return {
        label: 'Session Resumed',
        className: 'bg-blue-badge text-blue-badge-foreground',
        icon: RotateCw,
      };
  }
}

function formatDate(dateString: string) {
  return new Date(dateString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function formatUserAgent(userAgent: string | null) {
  if (!userAgent) return '-';
  // Extract browser and OS info for display
  if (userAgent.length > 60) {
    return userAgent.substring(0, 60) + '...';
  }
  return userAgent;
}

export default function AuthEventsPage() {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [eventTypeFilter, setEventTypeFilter] = useState<AuthEventType | ''>('');
  const [dateFilter, setDateFilter] = useState<'all' | '1h' | '24h' | '7d' | '30d'>('24h');

  // Calculate since date based on filter
  const getSinceDate = () => {
    const now = new Date();
    switch (dateFilter) {
      case '1h':
        return new Date(now.getTime() - 60 * 60 * 1000).toISOString();
      case '24h':
        return new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString();
      case '7d':
        return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
      case '30d':
        return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
      default:
        return undefined;
    }
  };

  const {
    data: response,
    isLoading,
    refetch,
    dataUpdatedAt,
  } = useQuery({
    queryKey: ['auth-events', eventTypeFilter, dateFilter],
    queryFn: () =>
      fetchAuthEvents({
        event_type: eventTypeFilter || undefined,
        since: getSinceDate(),
        limit: 200,
      }),
    enabled: user?.role === 'owner',
    refetchInterval: 30000, // Auto-refresh every 30 seconds
  });

  const events = response?.data || [];

  // Filter by search query (email)
  const filteredEvents = events.filter((event) => {
    if (!searchQuery.trim()) return true;
    return event.email.toLowerCase().includes(searchQuery.toLowerCase());
  });

  // Not authorized view for non-owners
  if (user?.role !== 'owner') {
    return (
      <div className="space-y-8">
        <div>
          <p className="text-sm uppercase tracking-wide text-muted-foreground">Admin</p>
          <h1 className="text-3xl font-bold">Auth Events</h1>
        </div>
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <Shield className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold">Access Restricted</h3>
            <p className="text-muted-foreground mt-2 max-w-sm">
              Only account owners can view auth events.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-sm uppercase tracking-wide text-muted-foreground">Admin</p>
          <h1 className="text-3xl font-bold">Auth Events</h1>
          <p className="text-muted-foreground">
            View login attempts and session activity across your organization.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          {dataUpdatedAt && (
            <span className="text-xs text-muted-foreground">
              Updated {new Date(dataUpdatedAt).toLocaleTimeString()}
            </span>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <SearchFilter
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Filter by email..."
          className="w-full max-w-sm"
        />

        <select
          value={eventTypeFilter}
          onChange={(e) => setEventTypeFilter(e.target.value as AuthEventType | '')}
          className="rounded-md border border-input bg-background px-3 py-2 text-sm"
        >
          <option value="">All Event Types</option>
          <option value="login_success">Login Success</option>
          <option value="login_failed">Login Failed</option>
          <option value="session_resumed">Session Resumed</option>
        </select>

        <select
          value={dateFilter}
          onChange={(e) => setDateFilter(e.target.value as typeof dateFilter)}
          className="rounded-md border border-input bg-background px-3 py-2 text-sm"
        >
          <option value="1h">Last Hour</option>
          <option value="24h">Last 24 Hours</option>
          <option value="7d">Last 7 Days</option>
          <option value="30d">Last 30 Days</option>
          <option value="all">All Time</option>
        </select>

        {(searchQuery || eventTypeFilter) && (
          <span className="text-sm text-muted-foreground">
            {filteredEvents.length} of {events.length} events
          </span>
        )}
      </div>

      {isLoading ? (
        <p>Loading...</p>
      ) : events.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <Shield className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold">No auth events found</h3>
            <p className="text-muted-foreground mt-2 max-w-sm">
              No authentication events match your current filters.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Time</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Event Type</TableHead>
                <TableHead>Failure Reason</TableHead>
                <TableHead>IP Address</TableHead>
                <TableHead className="hidden lg:table-cell">User Agent</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredEvents.map((event) => {
                const badge = getEventTypeBadge(event.event_type);
                const Icon = badge.icon;
                return (
                  <TableRow key={event.id}>
                    <TableCell className="text-muted-foreground whitespace-nowrap">
                      {formatDate(event.created_at)}
                    </TableCell>
                    <TableCell className="font-medium">{event.email}</TableCell>
                    <TableCell>
                      <span
                        className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ${badge.className}`}
                      >
                        <Icon className="h-3 w-3" />
                        {badge.label}
                      </span>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {event.failure_reason ? (
                        <span className="text-red-badge-foreground">
                          {event.failure_reason.replace(/_/g, ' ')}
                        </span>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground font-mono text-xs">
                      {event.ip_address || '-'}
                    </TableCell>
                    <TableCell
                      className="hidden lg:table-cell text-muted-foreground text-xs max-w-xs truncate"
                      title={event.user_agent || undefined}
                    >
                      {formatUserAgent(event.user_agent)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {response?.count !== undefined && response.count > filteredEvents.length && (
        <p className="text-sm text-muted-foreground text-center">
          Showing {filteredEvents.length} of {response.count} total events
        </p>
      )}
    </div>
  );
}
